import { createFileRoute, Link, redirect } from '@tanstack/react-router';

import { OneRosterAuth } from '@/components/auth/auth.context';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { StudentPlacementProvider } from '@/features/placements/components/student-placement/StudentPlacementProvider';
import { StudentPlacementRoot } from '@/features/placements/components/student-placement/StudentPlacementRoot';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import {
  AppPageHeader,
  BreadcrumbItemProps,
} from '@/features/shared/components/AppPageHeader';
import { getImpersonationStatus } from '@/hooks/useImpersonation';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';
import { UserRoles } from '@/lib/constants/auth.constants';

export const Route = createFileRoute('/_app/app/placements-old')({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    const allowedRoles = [UserRoles.ADMINISTRATOR, UserRoles.STUDENT];

    const auth = context.auth as OneRosterAuth;
    const { isImpersonated } = await getImpersonationStatus();

    const roles = auth?.user?.roles.map((role) => role.role) ?? [];
    const isAide = roles.includes(UserRoles.AIDE);

    if (isAide && isImpersonated) {
      allowedRoles.push(UserRoles.AIDE);
    }

    if (!roles.some((role) => allowedRoles.includes(role as UserRoles))) {
      throw redirect({ to: '/app/my-learning-report', replace: true });
    }
  },
  ssr: false,
});

const breadcrumbItems: BreadcrumbItemProps[] = [
  {
    label: 'App',
    link: { to: '/app' },
  },
  {
    label: 'Placements (old)',
    link: { to: '/app/placements-old' },
  },
];

function RouteComponent() {
  const { user } = useOneRosterAuth();

  if (!user) {
    return null;
  }

  return (
    <AppPageContent>
      <AppPageHeader
        title="Placements (deprecated)"
        breadcrumbItems={breadcrumbItems}
      />
      <div className="px-4 pt-4">
        <Alert variant="destructive">
          <AlertTitle>!!! Deprecated !!!</AlertTitle>
          <AlertDescription className="flex flex-row">
            This page is deprecated. Please use the{' '}
            <Link to="/app/placements" className="font-bold underline">
              new placements page
            </Link>{' '}
            instead.
          </AlertDescription>
        </Alert>
      </div>
      <StudentPlacementProvider user={user}>
        <StudentPlacementRoot />
      </StudentPlacementProvider>
    </AppPageContent>
  );
}
